/**
 * Client-side encryption utilities for member-specific data
 * Uses Web Crypto API for AES-256-GCM encryption
 */

const ALGORITHM = "AES-GCM";
const IV_LENGTH = 16; // 128 bits
const SALT_LENGTH = 64;
const KEY_LENGTH = 32; // 256 bits
const ITERATIONS = 100000; // PBKDF2 iterations

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert hex string to Uint8Array
 */
function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Derive an encryption key from the member's key using PBKDF2
 */
async function deriveKey(memberKey: string, salt: Uint8Array): Promise<CryptoKey> {
  // convert hex string -> Uint8Array -> ensure its backed by a real ArrayBuffer
  const rawKeyBytes = hexToUint8Array(memberKey);
  const keyData = rawKeyBytes.buffer instanceof ArrayBuffer ? rawKeyBytes.buffer : rawKeyBytes.slice().buffer;

  const keyMaterial = await crypto.subtle.importKey("raw", keyData, { name: "PBKDF2" }, false, ["deriveKey"]);

  const safeSalt = salt.slice();

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: safeSalt,
      iterations: ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH * 8 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt data for a specific member
 * @param data - The data to encrypt
 * @param memberKey - The member's encryption key (hex string)
 * @returns Encrypted data as base64 string with format: salt:iv:encryptedData
 */
export async function encryptForMember(data: string, memberKey: string): Promise<string> {
  if (!data) return data;
  if (!memberKey) {
    throw new Error("Member encryption key is required");
  }

  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Derive key from member key
  const key = await deriveKey(memberKey, salt);

  // Encrypt the data
  const encoder = new TextEncoder();
  const encodedData = encoder.encode(data);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: ALGORITHM,
      iv: iv,
    },
    key,
    encodedData,
  );

  // Return format: salt:iv:encryptedData (GCM includes auth tag in encrypted data)
  return `${arrayBufferToBase64(salt.buffer)}:${arrayBufferToBase64(iv.buffer)}:${arrayBufferToBase64(encrypted)}`;
}

/**
 * Decrypt data for a specific member
 * @param encryptedData - The encrypted data in format: salt:iv:encryptedData
 * @param memberKey - The member's encryption key (hex string)
 * @returns Decrypted data as string
 */
export async function decryptForMember(encryptedData: string, memberKey: string): Promise<string> {
  if (!encryptedData) return encryptedData;
  if (!memberKey) {
    throw new Error("Member encryption key is required");
  }

  try {
    // Parse the encrypted data format
    const parts = encryptedData.split(":");
    if (parts.length !== 3 && parts.length !== 4) {
      // Support both 3-part (client) and 4-part (server) formats
      throw new Error("Invalid encrypted data format");
    }

    let saltBase64: string, ivBase64: string, encryptedBase64: string;

    if (parts.length === 4) {
      // Server format: salt:iv:tag:encryptedData (tag is part of encrypted data in GCM)
      [saltBase64, ivBase64, , encryptedBase64] = parts;
    } else {
      // Client format: salt:iv:encryptedData
      [saltBase64, ivBase64, encryptedBase64] = parts;
    }

    // Convert from base64
    const salt = new Uint8Array(base64ToArrayBuffer(saltBase64));
    const iv = new Uint8Array(base64ToArrayBuffer(ivBase64));
    const encrypted = new Uint8Array(base64ToArrayBuffer(encryptedBase64));

    // Derive key from member key
    const key = await deriveKey(memberKey, salt);

    // Decrypt the data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv: iv,
      },
      key,
      encrypted,
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    throw new Error(`Failed to decrypt data: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

/**
 * Check if data appears to be encrypted
 */
export function isEncrypted(data: string): boolean {
  if (!data) return false;
  const parts = data.split(":");
  return parts.length === 3 || parts.length === 4;
}
